/**
 * Role Types and Definitions for MyClinicCore
 *
 * This file defines the multi-role system for access control across the application.
 * Users can have multiple roles simultaneously (e.g., Provider + HR Admin).
 *
 * See ROLE_DEFINITIONS.md for complete documentation.
 */

/**
 * Available user roles in the system
 */
export type UserRole =
  | 'system_admin'          // Full system access, can manage all users and roles
  | 'hr_admin'              // Manages HR documents and employee compliance
  | 'clinical_director'     // Oversees clinical operations and provider supervision
  | 'provider'              // Therapist/clinician providing patient care
  | 'administrative_staff'  // Office staff handling scheduling and billing
  | 'client';               // Patient receiving therapy services

/**
 * Dashboard types that users can access based on their roles
 */
export type DashboardType =
  | 'provider'
  | 'hr'
  | 'clinical'
  | 'admin'
  | 'client';

/**
 * Dashboard option for multi-role users to switch between views
 */
export interface DashboardOption {
  id: DashboardType;
  label: string;
  icon: string;
  description?: string;
}

/**
 * HR Document Types - organized by sensitivity level
 */
export type HRDocumentCategory = 'credentials' | 'compliance';

/**
 * Professional credentials (semi-public)
 * Viewable by: System Admin, HR Admin, Clinical Director, All Providers
 */
export type ProfessionalCredentialType =
  | 'license'               // LPC, LCSW, LMFT, etc.
  | 'certification'         // CBT, EMDR, specialized training
  | 'education'             // Degrees, diplomas
  | 'continuing_education'  // CE credits
  | 'malpractice_insurance' // Proof of coverage
  | 'npi_number';           // National Provider Identifier

/**
 * HR compliance documents (highly restricted)
 * Viewable by: System Admin, HR Admin, Document Owner only
 */
export type HRComplianceDocumentType =
  | 'i9'                    // Employment eligibility
  | 'w4'                    // Tax withholding
  | 'direct_deposit'        // Banking information
  | 'background_check'      // Background check report
  | 'ssn'                   // Social Security Number
  | 'emergency_contact'     // Emergency contact info
  | 'hipaa_training'        // HIPAA training certificate
  | 'handbook_ack'          // Employee handbook acknowledgment
  | 'nda'                   // Non-disclosure agreement
  | 'employment_contract';  // Employment contract

/**
 * All HR document types
 */
export type HRDocumentType = ProfessionalCredentialType | HRComplianceDocumentType;

/**
 * Role display names for UI
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  system_admin: 'System Administrator',
  hr_admin: 'HR Administrator',
  clinical_director: 'Clinical Director',
  provider: 'Provider',
  administrative_staff: 'Administrative Staff',
  client: 'Client'
};

/**
 * Role descriptions
 */
export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  system_admin: 'Full system access with user and role management',
  hr_admin: 'Manages employee HR documents and compliance',
  clinical_director: 'Oversees clinical operations and provider supervision',
  provider: 'Provides direct patient care and manages caseload',
  administrative_staff: 'Handles scheduling, billing, and office operations',
  client: 'Patient receiving therapy services'
};

/**
 * Map roles to their primary dashboard
 */
export const ROLE_DEFAULT_DASHBOARD: Record<UserRole, DashboardType> = {
  system_admin: 'provider',     // Most admins are also providers
  hr_admin: 'hr',
  clinical_director: 'clinical',
  provider: 'provider',
  administrative_staff: 'admin',
  client: 'client'
};

/**
 * Dashboard options metadata
 */
export const DASHBOARD_OPTIONS: Record<DashboardType, Omit<DashboardOption, 'id'>> = {
  provider: {
    label: 'Provider Dashboard',
    icon: 'Stethoscope',
    description: 'Manage patient caseload and clinical operations'
  },
  hr: {
    label: 'HR Management',
    icon: 'Users',
    description: 'Employee documents and compliance tracking'
  },
  clinical: {
    label: 'Clinical Director',
    icon: 'ClipboardCheck',
    description: 'Clinical oversight and provider supervision'
  },
  admin: {
    label: 'Administrative',
    icon: 'Calendar',
    description: 'Scheduling, billing, and office operations'
  },
  client: {
    label: 'My Portal',
    icon: 'User',
    description: 'Access your appointments and therapy resources'
  }
};

/**
 * Helper to check if a document type is a credential (semi-public)
 */
export function isCredentialDocument(docType: HRDocumentType): docType is ProfessionalCredentialType {
  return [
    'license',
    'certification',
    'education',
    'continuing_education',
    'malpractice_insurance',
    'npi_number'
  ].includes(docType);
}

/**
 * Helper to check if a document type is compliance (restricted)
 */
export function isComplianceDocument(docType: HRDocumentType): docType is HRComplianceDocumentType {
  return [
    'i9',
    'w4',
    'direct_deposit',
    'background_check',
    'ssn',
    'emergency_contact',
    'hipaa_training',
    'handbook_ack',
    'nda',
    'employment_contract'
  ].includes(docType);
}

/**
 * Get document category from type
 */
export function getDocumentCategory(docType: HRDocumentType): HRDocumentCategory {
  return isCredentialDocument(docType) ? 'credentials' : 'compliance';
}

/**
 * Get human-readable label for document type
 */
export const HR_DOCUMENT_LABELS: Record<HRDocumentType, string> = {
  // Credentials
  license: 'Professional License',
  certification: 'Certification',
  education: 'Educational Degree',
  continuing_education: 'Continuing Education',
  malpractice_insurance: 'Malpractice Insurance',
  npi_number: 'NPI Number',

  // Compliance
  i9: 'I-9 Employment Eligibility',
  w4: 'W-4 Tax Withholding',
  direct_deposit: 'Direct Deposit Form',
  background_check: 'Background Check',
  ssn: 'Social Security Number',
  emergency_contact: 'Emergency Contact',
  hipaa_training: 'HIPAA Training Certificate',
  handbook_ack: 'Employee Handbook Acknowledgment',
  nda: 'Non-Disclosure Agreement',
  employment_contract: 'Employment Contract'
};
